import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { CompetencyMap, Competency } from '../types';

// Хуки для карт компетенций
export const useCompetencyMaps = () => {
  return useQuery({
    queryKey: ['competency-maps'],
    queryFn: async (): Promise<CompetencyMap[]> => {
      return await api('/competency-maps');
    },
  });
};

// Lightweight list of every class map (id + name + professions) for the /grades
// class switcher. Available to any authenticated user, unlike the admin-only
// full list from useCompetencyMaps.
export interface CompetencyMapDirectoryEntry {
  _id: string;
  name: string;
  professions?: { _id: string; name: string }[];
  grades?: { _id: string; title: string }[];
}

export const useCompetencyMapDirectory = () => {
  return useQuery({
    queryKey: ['competency-maps', 'directory'],
    queryFn: async (): Promise<CompetencyMapDirectoryEntry[]> => {
      return await api('/competency-maps/directory');
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useCompetencyMap = (id: string) => {
  return useQuery({
    queryKey: ['competency-maps', id],
    queryFn: async (): Promise<CompetencyMap> => {
      return await api(`/competency-maps/${id}`);
    },
    enabled: !!id,
  });
};

export interface MapLayoutLink {
  competencyId: string;
  gradeId: string;
  swimlaneId: string;
  order: number;
}

// Сохранить раскладку карты после перетаскивания (грейд, свимлейн, порядок).
export const useSaveMapLayout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mapId, links }: { mapId: string; links: MapLayoutLink[] }) => {
      return await api(`/competency-maps/${mapId}/layout`, {
        method: 'PATCH',
        body: { links },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competency-maps'] });
    },
  });
};

// Хуки для компетенций
export const useCreateCompetency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      title: string;
      description: string;
      swimlaneId?: string;
      gradeId?: string;
      tags?: string[];
      mentors?: string[];
      domain?: string;
      competencyMapId?: string;
    }) => {
      return await api('/competency-maps/competencies', {
        method: 'POST',
        body: data,
      });
    },
    onSuccess: () => {
      // Инвалидируем все кеши карт компетенций и списка компетенций
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'competency-maps' || query.queryKey[0] === 'competencies',
      });
    },
  });
};

export const useUpdateCompetency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      _id,
      data,
    }: {
      _id: string;
      data: {
        title?: string;
        description?: string;
        swimlaneId?: string;
        gradeId?: string;
        tags?: string[];
        mentors?: string[];
        isRequired?: boolean;
        competencyMapId?: string;
        domain?: string;
      };
    }) => {
      return await api(`/competency-maps/competencies/${_id}`, {
        method: 'PATCH',
        body: data,
      });
    },
    onSuccess: () => {
      // Инвалидируем все кеши карт компетенций и списка компетенций
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'competency-maps' || query.queryKey[0] === 'competencies',
      });
    },
  });
};

// Хук для получения всех компетенций
export const useCompetencies = () => {
  return useQuery({
    queryKey: ['competencies'],
    queryFn: async (): Promise<Competency[]> => {
      return await api('/competencies');
    },
  });
};

// Хук для добавления компетенций в карту
export const useAddCompetenciesToMap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      mapId,
      competencies,
    }: {
      mapId: string;
      competencies: Array<{
        competencyId: string;
        gradeId: string;
        swimlaneId: string;
      }>;
    }) => {
      return await api(`/competency-maps/${mapId}/competencies`, {
        method: 'POST',
        body: { competencies },
      });
    },
    onSuccess: () => {
      // Инвалидируем все кеши карт компетенций и списка компетенций
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'competency-maps' || query.queryKey[0] === 'competencies',
      });
    },
  });
};

// Хук для удаления компетенции из карты
export const useRemoveCompetencyFromMap = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ competencyId, mapId }: { competencyId: string; mapId: string }) => {
      return await api(`/competency-maps/competencies/${competencyId}/from-map?mapId=${mapId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      // Инвалидируем все кеши карт компетенций и списка компетенций
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'competency-maps' || query.queryKey[0] === 'competencies',
      });
    },
  });
};

// Хук для получения карт, в которых используется компетенция
export const useCompetencyMapsForCompetency = (competencyId: string | undefined) => {
  return useQuery({
    queryKey: ['competency-maps', 'competency', competencyId],
    queryFn: async (): Promise<{ _id: string; name: string }[]> => {
      if (!competencyId) return [];
      return await api(`/competency-maps/competencies/${competencyId}/maps`);
    },
    enabled: !!competencyId,
  });
};

// Хук для полного удаления компетенции
export const useDeleteCompetency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (competencyId: string) => {
      return await api(`/competency-maps/competencies/${competencyId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      // Инвалидируем все кеши компетенций и карт
      queryClient.invalidateQueries({ queryKey: ['competencies'] });
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'competency-maps',
      });
    },
  });
};

// Генерация описания компетенции через ai-service (ответ приходит целиком).
export const useGenerateCompetencyDescription = () => {
  return useMutation({
    mutationFn: async (data: {
      competencyName: string;
      gradeName?: string;
      swimlaneName?: string;
      /** Бэкенд возьмёт из карты профессии, чтобы описание было под них. */
      competencyMapId?: string;
    }): Promise<{ description: string; success: boolean }> => {
      return await api('/competencies/generate-description', {
        method: 'POST',
        body: data,
      });
    },
  });
};

// Описание грейда: грейд общий для всех профессий, уходят только его поля.
export const useGenerateGradeDescription = () => {
  return useMutation({
    mutationFn: async (data: {
      title: string;
      qualification?: string;
      position?: string;
    }): Promise<{ description: string; success: boolean }> => {
      return await api('/grades/generate-description', { method: 'POST', body: data });
    },
  });
};

export const useUpdateDependencies = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      competencyId,
      dependencies,
    }: {
      competencyId: string;
      dependencies: string[];
    }) => {
      return await api(`/competency-maps/competencies/${competencyId}/dependencies`, {
        method: 'PATCH',
        body: { dependencies },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'competency-maps',
      });
    },
  });
};

/** Компетенция ИИ-черновика карты: грейд и свимлейн уже сопоставлены с картой. */
export interface MapDraftItem {
  title: string;
  description: string;
  gradeId: string;
  swimlaneId: string;
}

// ИИ-черновик карты: генерация ничего не пишет, ответ целиком за 10–60 с.
export const useGenerateMapDraft = (mapId: string) =>
  useMutation({
    mutationFn: (): Promise<{ competencies: MapDraftItem[] }> =>
      api(`/competency-maps/${mapId}/draft/generate`, { method: 'POST' }),
  });

export const useSaveMapDraft = (mapId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (competencies: MapDraftItem[]): Promise<{ created: number; skipped: number }> =>
      api(`/competency-maps/${mapId}/draft`, { method: 'POST', body: { competencies } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['competency-maps', mapId] }),
  });
};

/** Компетенция черновика первой карты: уровень и направление — названиями, их ещё нет в базе. */
export interface FirstMapItem {
  title: string;
  description: string;
  grade: string;
  swimlane: string;
}

// Мастер «Первая карта»: черновик по профессии и шкале, ничего не пишет.
export const useFirstMapDraft = () =>
  useMutation({
    mutationFn: (body: {
      profession: string;
      grades: string[];
      swimlanes?: string[];
    }): Promise<{ swimlanes: string[]; competencies: FirstMapItem[] }> =>
      api('/competency-maps/first/generate', { method: 'POST', body }),
  });

export const useCreateFirstMap = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      profession: string;
      grades: string[];
      competencies: FirstMapItem[];
    }): Promise<{ id: string }> => api('/competency-maps/first', { method: 'POST', body }),
    onSuccess: () => {
      for (const key of ['competency-maps', 'grades', 'swimlanes', 'professions'])
        queryClient.invalidateQueries({ queryKey: [key] });
    },
  });
};
